import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import { dataUpTo } from './LensBoard';
import filterSelectionSummary from './filterSelectionSummary';
import type { Envelope, LensDocument, LensDocumentData, LensState } from './types';

type LensAboutProps = {
  document: LensDocument;
  data?: LensDocumentData;
  state: LensState;
  filterLabel: (column: string) => string;
};

const KIND_WORDS = {
  catalogue: 'Governed dataset',
  sql: 'SQL written for this Lens',
  table: 'A table kept in this Lens',
} as const;

export function Pill({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="span"
      sx={{
        bgcolor: 'action.hover',
        borderRadius: '8px',
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.6,
        px: 1,
      }}
    >
      {children}
    </Typography>
  );
}

export function periodLine(data?: LensDocumentData): string | undefined {
  if (!data?.period) return undefined;
  const { current, comparisons } = data.period;
  const against = comparisons
    .map((window) =>
      window.id === 'ly' ? 'the same period last year' : window.id === 'l2y' ? 'two years ago' : window.label,
    )
    .join(' and ');
  return `${current.label}${comparisons.length ? ` vs ${against}` : ''}`;
}

export function Heading({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ borderTop: 1, borderColor: 'divider', fontSize: 13, fontWeight: 600, pt: 1.5 }}>
      {children}
    </Typography>
  );
}

export default function LensAbout({ document, data, state, filterLabel }: LensAboutProps) {
  const fields = useGetReportingLensFields(data?.lensId ?? '', data?.lensVersion);
  const upTo = dataUpTo(Object.values(data?.queries ?? {}) as Array<Envelope>);
  const selectionSummary = (column: string, values: Array<string>) =>
    filterSelectionSummary(values, data?.options?.[column], data?.optionMeta?.[column]?.status === 'complete');
  const chosen = [
    ...Object.entries(state.filters ?? {}).map(
      ([column, values]) => `${filterLabel(column)}: ${selectionSummary(column, values)}`,
    ),
    ...Object.entries(state.ranges ?? {}).map(
      ([column, range]) => `${filterLabel(column)}: ${range.min ?? '…'} to ${range.max ?? '…'}`,
    ),
    ...Object.entries(state.select ?? {}).map(
      ([column, values]) => `${filterLabel(column)}: ${selectionSummary(column, values)} (picked on the board)`,
    ),
  ];
  const defaults = document.filters
    .filter((filter) => filter.value?.length && !state.filters?.[filter.column])
    .map((filter) => `${filterLabel(filter.column)}: ${selectionSummary(filter.column, filter.value!)}`);
  const shown = [
    ...new Map(
      (Object.values(data?.queries ?? {}) as Array<Envelope>).flatMap((envelope) =>
        envelope.columns.filter((column) => column.role === 'measure').map((column) => [column.name, column] as const),
      ),
    ).values(),
  ];
  return (
    <Stack data-lens-about spacing={1.5} sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
        {document.title}
      </Typography>
      {document.lead ? <Typography variant="body2">{document.lead}</Typography> : null}
      <Typography color="text.secondary" variant="body2">
        {[periodLine(data), upTo ? `data up to ${upTo}` : undefined].filter(Boolean).join(' · ')}
      </Typography>
      <Heading>Filters in effect</Heading>
      {[...defaults, ...chosen].length ? (
        [...defaults, ...chosen].map((line) => (
          <Typography key={line} variant="body2">
            {line}
          </Typography>
        ))
      ) : (
        <Typography color="text.secondary" variant="body2">
          None. Every row in the period.
        </Typography>
      )}
      <Heading>Where the numbers come from</Heading>
      {fields.isLoading ? <Box aria-busy sx={{ bgcolor: 'action.hover', borderRadius: '8px', height: 80 }} /> : null}
      {(fields.data?.datasets ?? []).map((dataset) => (
        <Stack key={dataset.id} spacing={0.5}>
          <Box>
            <Pill>{dataset.label}</Pill>
          </Box>
          <Typography color="text.secondary" variant="caption">
            {KIND_WORDS[dataset.kind]}
          </Typography>
        </Stack>
      ))}
      <Heading>Measures on this Lens</Heading>
      {shown.map((column) => (
        <Box key={column.name}>
          <Stack alignItems="center" direction="row" spacing={0.5}>
            <Typography variant="body2">{column.label}</Typography>
            {column.certified ? <Pill>Certified</Pill> : null}
          </Stack>
          <Typography color="text.secondary" component="div" variant="caption">
            {column.definition ??
              (column.certified
                ? 'Certified in the governed catalogue.'
                : 'No definition is set for this measure yet.')}
          </Typography>
          <Typography color="text.secondary" component="div" sx={{ fontSize: 12 }}>
            Called “{column.name}” in the data service.
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
